import { defineStore } from 'pinia'
import { createPlayer } from '../models/player'
import { buildRoundPlan, roundPoints } from '../models/boerenbridge'

function defaultState() {
  return {
    // null = geen Boerenbridge-spel actief; de app toont dan het andere spel
    view: null,
    players: [],
    roundPlan: [],
    roundNumber: 1,
    roundStarted: false,
    // bod van de ronde die bezig is: { trump, bids: { [playerId]: aantal } }
    currentBid: null,
    // gehaalde slagen van de ronde die bezig is: { [playerId]: aantal }
    currentTricks: null,
    roundHistory: [],
    editingRoundNumber: null,
    confirmingEndGame: false
  }
}

export const useBoerenbridgeStore = defineStore('boerenbridge', {
  state: () => {
    const saved = sessionStorage.getItem('boerenbridge')
    const persisted = saved ? JSON.parse(saved) : {}
    return { ...defaultState(), ...persisted, confirmingEndGame: false }
  },

  getters: {
    playerNames: (state) => state.players.map(p => p.name),
    hasSavedGame: (state) => state.roundStarted,
    totalRounds: (state) => state.roundPlan.length,
    isFinished: (state) => state.roundPlan.length > 0 && state.roundNumber > state.roundPlan.length,

    /** Ronde die op dit moment ingevuld wordt (kan een correctie van een oude ronde zijn). */
    activeRoundNumber: (state) => state.editingRoundNumber ?? state.roundNumber,

    cardsForRound: (state) => (roundNumber) => state.roundPlan[roundNumber - 1] ?? 0,

    activeCards() {
      return this.cardsForRound(this.activeRoundNumber)
    },

    /** De deler schuift elke ronde één plek op; speler 0 deelt de eerste ronde. */
    dealerIdForRound: (state) => (roundNumber) => {
      if (!state.players.length) return null
      return state.players[(roundNumber - 1) % state.players.length].id
    },

    activeDealerId() {
      return this.dealerIdForRound(this.activeRoundNumber)
    },

    /** Biedvolgorde: links van de deler begint, de deler biedt als laatste. */
    biddingOrder() {
      const players = this.players
      if (!players.length) return []
      const dealerIndex = players.findIndex(p => p.id === this.activeDealerId)
      const start = (dealerIndex + 1) % players.length
      return players.slice(start).concat(players.slice(0, start))
    },

    playerTotals: (state) => {
      const totals = Object.fromEntries(state.players.map(p => [p.id, 0]))
      for (const round of state.roundHistory) {
        for (const entry of round.entries) {
          if (entry.id in totals) totals[entry.id] += entry.points
        }
      }
      return totals
    },

    /** Spelers met hun actuele totaal, voor de scorebalk. */
    playersWithScore() {
      const totals = this.playerTotals
      return this.players.map(p => ({ ...p, score: totals[p.id] ?? 0 }))
    },

    /** Rijen voor het scoreoverzicht: per ronde bod, gehaald en punten per speler. */
    scoreTable: (state) => ({
      players: state.players.map(p => ({ id: p.id, name: p.name })),
      rows: state.roundHistory.map(round => {
        const byId = Object.fromEntries(round.entries.map(e => [e.id, e]))
        return {
          roundNumber: round.roundNumber,
          cards: round.cards,
          trump: round.trump,
          entries: state.players.map(p => ({
            id: p.id,
            bid: byId[p.id]?.bid ?? 0,
            tricks: byId[p.id]?.tricks ?? 0,
            points: byId[p.id]?.points ?? 0
          }))
        }
      })
    }),

    /** Eindstand, hoogste score eerst. */
    ranking() {
      const totals = this.playerTotals
      return this.players
        .map(p => ({ id: p.id, name: p.name, score: totals[p.id] ?? 0 }))
        .sort((a, b) => b.score - a.score)
    }
  },

  actions: {
    setView(view) {
      this.view = view
    },

    /** Zet het spel volledig terug; de app valt terug op het andere spel. */
    resetGame() {
      this.$patch(defaultState())
    },

    /**
     * Zet een geimporteerde spelstand terug. Alleen bekende velden worden
     * overgenomen, zodat een bestand geen onbekende state kan binnenbrengen.
     */
    importState(persisted) {
      const base = defaultState()
      const next = { ...base }
      for (const key of Object.keys(base)) {
        if (key === 'confirmingEndGame') continue
        if (Object.prototype.hasOwnProperty.call(persisted, key)) next[key] = persisted[key]
      }
      this.$patch(next)
    },

    startNewGame() {
      this.$patch({ ...defaultState(), view: 'setup' })
    },

    restoreGame() {
      this.view = this.isFinished ? 'finished' : 'scoreboard'
    },

    confirmPlayers(names, peakCards) {
      this.players = names.map((name, id) => createPlayer(id, name.trim()))
      this.roundPlan = buildRoundPlan(names.length, peakCards)
      this.roundNumber = 1
      this.roundHistory = []
      this._clearRoundDraft()
      this.view = 'dealer'
    },

    /** De gekozen deler wordt speler 0, zodat de rotatie bij hem begint. */
    confirmDealer(dealerIndex) {
      const names = this.playerNames
      this.players = names
        .slice(dealerIndex)
        .concat(names.slice(0, dealerIndex))
        .map((name, id) => createPlayer(id, name))
      this.view = 'scoreboard'
    },

    _clearRoundDraft() {
      this.currentBid = null
      this.currentTricks = null
    },

    /**
     * Zorgt dat er een ronde-invoer klaarstaat. Bod en slagen leven in de store
     * (en dus in sessionStorage), zodat heen en weer navigeren of een refresh
     * niets wist.
     */
    ensureRoundDraft() {
      if (!this.currentBid) this.currentBid = { trump: null, bids: {} }
      if (!this.currentTricks) this.currentTricks = {}
    },

    startRound() {
      this.editingRoundNumber = null
      this._clearRoundDraft()
      this.ensureRoundDraft()
      this.view = 'bidding'
    },

    cancelRound() {
      this.editingRoundNumber = null
      this._clearRoundDraft()
      this.view = 'scoreboard'
    },

    setTrump(trump) {
      this.ensureRoundDraft()
      this.currentBid.trump = trump
    },

    setBid(playerId, bid) {
      this.ensureRoundDraft()
      this.currentBid.bids[playerId] = bid
    },

    setTricks(playerId, tricks) {
      this.ensureRoundDraft()
      this.currentTricks[playerId] = tricks
    },

    /** Tussenstap: iedereen ziet eerst wat er geboden is. */
    goToOverview() {
      this.view = 'overview'
    },

    goToResult() {
      this.view = 'result'
    },

    backToOverview() {
      this.view = 'overview'
    },

    backToBidding() {
      this.view = 'bidding'
    },

    _buildEntries(bids, tricks) {
      return this.players.map(p => {
        const bid = bids[p.id] ?? 0
        const taken = tricks[p.id] ?? 0
        return { id: p.id, bid, tricks: taken, points: roundPoints(bid, taken) }
      })
    },

    recordRoundResult() {
      if (!this.currentBid || !this.currentTricks) return
      this.roundHistory.push({
        roundNumber: this.roundNumber,
        cards: this.roundPlan[this.roundNumber - 1],
        trump: this.currentBid.trump,
        entries: this._buildEntries(this.currentBid.bids, this.currentTricks)
      })
      this.roundStarted = true
      this.roundNumber++
      this._clearRoundDraft()
      this.view = this.isFinished ? 'finished' : 'scoreboard'
    },

    /** Zet bod én resultaat van de laatste ronde terug in de invoer. */
    startEditLastRound() {
      const last = this.roundHistory[this.roundHistory.length - 1]
      if (!last) return
      this.editingRoundNumber = last.roundNumber
      this.currentBid = {
        trump: last.trump,
        bids: Object.fromEntries(last.entries.map(e => [e.id, e.bid]))
      }
      this.currentTricks = Object.fromEntries(last.entries.map(e => [e.id, e.tricks]))
      this.view = 'result'
    },

    cancelEdit() {
      this.editingRoundNumber = null
      this._clearRoundDraft()
      this.view = this.isFinished ? 'finished' : 'scoreboard'
    },

    updateLastRoundResult() {
      const index = this.roundHistory.length - 1
      const last = this.roundHistory[index]
      if (!last || !this.currentBid || !this.currentTricks) return
      this.roundHistory[index] = {
        ...last,
        trump: this.currentBid.trump,
        entries: this._buildEntries(this.currentBid.bids, this.currentTricks)
      }
      this.editingRoundNumber = null
      this._clearRoundDraft()
      this.view = this.isFinished ? 'finished' : 'scoreboard'
    },

    requestEndGame() {
      this.confirmingEndGame = true
    },

    cancelEndGame() {
      this.confirmingEndGame = false
    },

    endGame() {
      this.resetGame()
    }
  }
})
